// Preise einer Liste (Raster), Preis setzen und beenden, Verlauf je Artikel, Preisauskunft, Massenänderung mit
// Vorschau und Freigabe, Import. Ein wirksamer Preis wird nie überschrieben: Der neue Preis ist eine neue Zeile ab
// Stichtag, der alte endet am Vortag.
import { PERMISSION_COSTS_READ, defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import {
  ROUNDING_RULES,
  zBasisPoints,
  zById,
  zDay,
  zLocationRef,
  zMarginInfo,
  zMinQty,
  zOptionalText,
  zPerson,
  zPriceCents,
  zPriceMode,
  zPricePerQty,
  zReason,
  zText,
  zUnitQty,
  zUnitRef,
} from './common'

const READ = 'pricing.price.read'
const WRITE = 'pricing.price.write'
const APPROVE = 'pricing.price.approve'

// ---- Preise einer Liste ----------------------------------------------------------------------------------

export const zPriceTier = z.object({ minQty: z.int(), amountCents: z.int(), pricePerQty: z.int(), validFrom: z.string() })

export const zPriceGridRow = z.object({
  /** item_unit.id – Schlüssel der Zeile */
  itemUnitId: zId,
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  itemGroupName: z.string().nullable(),
  unit: zUnitRef,
  /** Grundpreis der Liste am Stichtag (Staffel 0); null = kein Preis hinterlegt */
  amountCents: z.int().nullable(),
  pricePerQty: z.int(),
  validFrom: z.string().nullable(),
  /** Staffeln über dem Grundpreis am Stichtag */
  tiers: z.array(zPriceTier),
  /** nächster, erst künftig gültiger Grundpreis */
  upcoming: z.object({ amountCents: z.int(), validFrom: z.string() }).nullable(),
  /** derselbe Preis netto und brutto je Preiseinheit; null ohne Steuersatz */
  netCents: z.int().nullable(),
  grossCents: z.int().nullable(),
  /** „1,98 €/l“ – Grundpreis nach PAngV aus dem Preis ohne Pfand */
  basePriceText: z.string().nullable(),
  /** Pfand je Gebinde in Cent (Basis der Pfandart) – steht neben dem Preis, nie darin */
  depositCents: z.int().nullable(),
  margin: zMarginInfo.optional(),
})
export type PriceGridRow = z.output<typeof zPriceGridRow>

export const priceListPrices = defineContract({
  name: 'pricing.priceList.prices',
  title: 'Preise einer Preisliste anzeigen',
  description: 'Die Preise einer Liste je Artikel und Gebinde an einem Stichtag – mit Staffeln, künftigem Preis und Grundpreis.',
  llm: {
    description:
      'Liefert die Preise EINER Preisliste je Artikel-Gebinde am Stichtag (`date`, Standard heute): Grundpreis der ' +
      'Liste in Cent je Preiseinheit, Staffeln (ab Menge in Tausendstel des Gebindes), den nächsten künftigen Preis, ' +
      'netto/brutto und Grundpreis. Gebinde ohne Preis erscheinen mit `amountCents: null`. Filter: ' +
      'Warengruppe, Lieferant, Suchtext, `onlyMissing`. Einkaufspreis und Spanne (`margin`) nur mit dem Recht ' +
      '„Einstandspreise sehen“. Für die Frage „was zahlt Kunde X“ pricing.price.quote verwenden.',
    keywords: ['Preise', 'Preisliste', 'Staffelpreise', 'Spanne', 'Kalkulation', 'Rohertrag'],
  },
  input: listInput({
    sortBy: ['name', 'sku'],
    filters: {
      priceListId: zId,
      itemGroupId: zId.optional(),
      supplierId: zId.optional(),
      date: zDay.optional(),
      onlyMissing: z.boolean().optional(),
      onlyPriced: z.boolean().optional(),
    },
  }),
  output: listOutput(zPriceGridRow).extend({ date: z.string(), priceMode: zPriceMode }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { 'rows.*.margin': PERMISSION_COSTS_READ },
})

const zPriceEntry = z.strictObject({
  itemUnitId: zId,
  /** Staffel: ab dieser Menge im Gebinde in Tausendstel (0 = Grundpreis) */
  minQty: zMinQty.default(0),
  amountCents: zPriceCents,
  pricePerQty: zPricePerQty.default(1000),
})

export const priceSet = defineContract({
  name: 'pricing.price.set',
  title: 'Preise setzen',
  description: 'Setzt Preise einer Preisliste ab einem Stichtag. Bisherige Preise enden am Vortag und bleiben im Verlauf.',
  llm: {
    description:
      'Setzt in EINER Preisliste Preise je Artikel-Gebinde ab `validFrom` (Standard heute, nie in der Vergangenheit). ' +
      '`amountCents` ist der Preis in Cent je `pricePerQty` (1000 = je Gebinde) in der Basis der Liste (brutto oder ' +
      'netto – siehe pricing.priceList.get). `minQty` > 0 legt eine Staffel an (ab so vielen Gebinden, Tausendstel). ' +
      'Der bisherige Preis wird nicht überschrieben, sondern endet am Vortag. Für viele Artikel nach einer Regel ' +
      '(+3 %, Rundung) pricing.price.bulkApply verwenden; für befristete Aktionspreise pricing.promotion.*.',
    keywords: ['Preis ändern', 'Preis setzen', 'neuer Preis', 'Preiserhöhung', 'Staffelpreis', 'VK ändern'],
  },
  input: z.strictObject({
    priceListId: zId,
    validFrom: zDay.optional(),
    prices: z.array(zPriceEntry).min(1, { error: 'Bitte mindestens einen Preis angeben.' }).max(500),
    note: zOptionalText(300),
  }),
  output: z.object({
    validFrom: z.string(),
    /** neu angelegte oder korrigierte Preiszeilen */
    changedCount: z.int(),
    /** Preise, die schon so galten */
    unchangedCount: z.int(),
    /** vorgemerkte Regaletiketten */
    labelCount: z.int(),
  }),
  permission: WRITE,
  risk: 'financial',
  limits: { maxItems: 500, itemCount: (input) => input.prices.length },
})

export const priceEnd = defineContract({
  name: 'pricing.price.end',
  title: 'Preis beenden',
  description: 'Beendet den Preis eines Gebindes in einer Preisliste zu einem Tag; künftige Preise entfallen.',
  llm: {
    description:
      'Beendet den Preis eines Artikel-Gebindes in einer Preisliste zum Tag `validTo` (Standard gestern, also ab ' +
      'heute kein Preis mehr in dieser Liste) und entfernt künftige, noch nicht wirksame Preise. Ohne `minQty` alle ' +
      'Staffeln, mit `minQty` nur diese Staffel. Die Preisfindung fällt danach auf die nächste passende Liste zurück.',
    keywords: ['Preis beenden', 'Preis entfernen', 'Staffel löschen', 'auslisten'],
  },
  input: z.strictObject({ priceListId: zId, itemUnitId: zId, minQty: zMinQty.optional(), validTo: zDay.optional() }),
  output: z.object({ endedCount: z.int(), removedCount: z.int() }),
  permission: WRITE,
  risk: 'destructive',
})

export const PRICE_ROW_STATES = ['past', 'current', 'future'] as const

export const priceHistory = defineContract({
  name: 'pricing.price.history',
  title: 'Preisverlauf eines Artikels anzeigen',
  description: 'Alle Preise eines Artikels über alle Preislisten und Aktionen – vergangene, heutige und künftige.',
  llm: {
    description:
      'Liefert den Preisverlauf EINES Artikels: jede Preiszeile mit Preisliste, Gebinde, Staffel, Betrag, gültig von/bis ' +
      'und wer sie angelegt hat – auch beendete und künftige Preise und Aktionen. Mit `priceListId` nur eine Liste. ' +
      'Einkaufspreis und Spanne zum Stichtag der Zeile nur mit „Einstandspreise sehen“.',
    keywords: ['Preisverlauf', 'Preishistorie', 'alter Preis', 'seit wann', 'Preisänderung'],
  },
  input: z.strictObject({ itemId: zId, priceListId: zId.optional() }),
  output: z.object({
    item: z.object({ id: zId, sku: z.string(), name: z.string() }),
    rows: z.array(
      z.object({
        id: zId,
        priceList: z.object({ id: zId, name: z.string(), priceMode: zPriceMode, isPromotion: z.boolean(), active: z.boolean() }),
        unit: zUnitRef,
        minQty: z.int(),
        amountCents: z.int(),
        pricePerQty: z.int(),
        validFrom: z.string(),
        validTo: z.string().nullable(),
        state: z.enum(PRICE_ROW_STATES),
        createdBy: zPerson.nullable(),
        createdAt: z.date(),
        margin: zMarginInfo.optional(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { 'rows.*.margin': PERMISSION_COSTS_READ },
})

// ---- Preisauskunft ---------------------------------------------------------------------------------------

export const priceQuote = defineContract({
  name: 'pricing.price.quote',
  title: 'Preisauskunft',
  description: 'Was zahlt ein Kunde für einen Artikel in einem Gebinde bei einer Menge an einem Standort an einem Tag – mit Begründung.',
  llm: {
    description:
      'Beantwortet „was zahlt Kunde X für Artikel Y“: Preis aus der Preisfindung des Systems für Artikel, Gebinde ' +
      '(`unitId`), Menge im Gebinde (`qty` in Tausendstel, 12 Kartons = 12000), wahlweise Kunde ODER Preisgruppe, ' +
      'Standort und Tag (Standard heute). Die Antwort nennt Quelle (Liste, Staffel, Aktion, Kundensonderpreis), den ' +
      'regulären Listenpreis, die nächste Staffel, netto/brutto und den Grundpreis. Ohne ' +
      'Kunde und Preisgruppe gilt die Standard-Preisgruppe (Filialpreis). Nur lesen – ändert nichts.',
    keywords: ['Preisauskunft', 'was kostet', 'was zahlt', 'Preis für Kunde', 'Aktionspreis', 'Staffel', 'Grundpreis'],
  },
  input: z.strictObject({
    itemId: zId,
    unitId: zId.nullish(),
    qty: zUnitQty.default(1000).describe('Menge im Gebinde in Tausendstel (1 Karton = 1000) – entscheidet die Staffel'),
    partnerId: zId.nullish(),
    priceGroupId: zId.nullish(),
    locationId: zId.nullish(),
    date: zDay.optional(),
  }),
  output: z.object({
    date: z.string(),
    item: z.object({ id: zId, sku: z.string(), name: z.string() }),
    unit: zUnitRef,
    qty: z.int(),
    partner: z.object({ id: zId, number: z.string(), name: z.string() }).nullable(),
    priceGroup: z.object({ id: zId, name: z.string(), priceMode: zPriceMode }).nullable(),
    location: zLocationRef.nullable(),
    /** false: Für diese Anfrage ist kein Preis hinterlegt */
    found: z.boolean(),
    /** Preis je Preiseinheit in der Basis der Liste */
    amountCents: z.int().nullable(),
    pricePerQty: z.int().nullable(),
    priceMode: zPriceMode.nullable(),
    netCents: z.int().nullable(),
    grossCents: z.int().nullable(),
    taxRateBp: z.int().nullable(),
    /** Preis × Menge in der Basis der Liste (ohne Pfand) */
    lineCents: z.int().nullable(),
    /** customer | promotion | group | base */
    sourceKind: z.string().nullable(),
    /** „Aktionspreis 13.–19.10.“ */
    sourceLabel: z.string().nullable(),
    priceList: z.object({ id: zId, name: z.string() }).nullable(),
    /** erreichte Staffel (0 = Grundpreis) */
    tierMinQty: z.int().nullable(),
    /** regulärer Preis ohne Aktion und Sonderpreis */
    listPrice: z.object({ amountCents: z.int(), pricePerQty: z.int(), priceMode: zPriceMode, priceListName: z.string() }).nullable(),
    /** niedrigster Preis der 30 Tage vor Aktionsbeginn (PAngV § 11) – der zulässige Streichpreis */
    referencePrice: z.object({ amountCents: z.int(), grossCents: z.int().nullable(), date: z.string(), priceListName: z.string() }).nullable(),
    nextTier: z.object({ minQty: z.int(), amountCents: z.int(), pricePerQty: z.int() }).nullable(),
    basePriceText: z.string().nullable(),
    /** Pfand der angefragten Menge und je Gebinde, getrennt vom Preis */
    deposit: z.object({ name: z.string(), unitCents: z.int(), amountCents: z.int() }).nullable(),
    /** Sätze der Begründung in Lesereihenfolge */
    explanation: z.array(z.string()),
    margin: zMarginInfo.optional(),
  }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { margin: PERMISSION_COSTS_READ },
})

// ---- Massenänderung --------------------------------------------------------------------------------------

/** percent: ± Prozent (Basispunkte) · amount: ± Cent je Gebinde · margin: Preis aus Einkaufspreis und Zielspanne */
export const BULK_MODES = ['percent', 'amount', 'margin'] as const

const zBulkRule = z.strictObject({
  priceListId: zId,
  /** Auswahl: Warengruppe, Lieferant und/oder ausdrücklich genannte Gebinde (alles leer = ganze Liste) */
  itemGroupId: zId.nullish(),
  supplierId: zId.nullish(),
  itemUnitIds: z.array(zId).max(2000).optional(),
  mode: z.enum(BULK_MODES, { error: 'Bitte Prozent, Betrag oder Zielspanne wählen.' }),
  /** percent: Änderung in Basispunkten (+300 = +3 %) · margin: Zielspanne in Basispunkten */
  valueBp: z.int().min(-9000, { error: 'Mehr als −90 % sind nicht möglich.' }).max(100_000).optional(),
  /** amount: Änderung in Cent je Gebinde (+60 = +0,60 €), in der Basis der Liste */
  amountCents: z.int().min(-10_000_000).max(10_000_000).optional(),
  rounding: z.enum(ROUNDING_RULES).default('none'),
  /** Staffelpreise im selben Verhältnis mitziehen */
  includeTiers: z.boolean().default(true),
  validFrom: zDay.optional(),
  /** Warnung, wenn die Spanne nach der Änderung darunter liegt (Basispunkte) */
  minMarginBp: zBasisPoints.optional(),
})

export type InputOfBulk = z.output<typeof zBulkRule>

const zBulkRow = z.object({
  itemUnitId: zId,
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitRef,
  minQty: z.int(),
  pricePerQty: z.int(),
  oldCents: z.int(),
  newCents: z.int(),
  /** Änderung in Basispunkten des alten Preises; null bei altem Preis 0 */
  changeBp: z.int().nullable(),
  marginBefore: zMarginInfo.optional(),
  marginAfter: zMarginInfo.optional(),
  /** deutsche Hinweise zur Zeile („Spanne unter 15 %“, „unter Einstand“) */
  warnings: z.array(z.string()),
})

const zBulkSummary = z.object({
  priceList: z.object({ id: zId, name: z.string(), priceMode: zPriceMode }),
  validFrom: z.string(),
  /** Zeilen mit geändertem Preis */
  changedCount: z.int(),
  unchangedCount: z.int(),
  /** Gebinde der Auswahl ohne Preis in dieser Liste (bei Zielspanne: ohne Einkaufspreis) */
  skippedCount: z.int(),
  warningCount: z.int(),
})

export const priceBulkPreview = defineContract({
  name: 'pricing.price.bulkPreview',
  title: 'Massenänderung von Preisen vorrechnen',
  description: 'Rechnet eine Preisänderung für eine Auswahl vor: alter und neuer Preis, Rundung, Spanne – ohne etwas zu ändern.',
  llm: {
    description:
      'Rechnet eine Massenänderung vor, OHNE etwas zu speichern: für eine Preisliste und eine Auswahl (Warengruppe, ' +
      'Lieferant oder genannte Gebinde) die Regel `mode` percent (`valueBp`, +300 = +3 %), amount (`amountCents` je ' +
      'Gebinde) oder margin (`valueBp` = Zielspanne vom Netto-Erlös aus dem aktuellen Einkaufspreis), danach die ' +
      'Rundung (x9 = auf …,x9 · x0 = auf volle 10 Cent · x5 · x99 = auf …,99). Liefert je Zeile alten und neuen Preis ' +
      'und Warnungen; Einkaufspreis und Spanne nur mit „Einstandspreise sehen“. Zum Anwenden dieselbe Eingabe an ' +
      'pricing.price.bulkApply geben.',
    keywords: ['Massenänderung', 'Preiserhöhung', 'Preise anheben', 'prozentual', 'Kalkulation', 'Rundung', 'Vorschau'],
  },
  input: zBulkRule,
  output: zBulkSummary.extend({ rows: z.array(zBulkRow) }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { 'rows.*.marginBefore': PERMISSION_COSTS_READ, 'rows.*.marginAfter': PERMISSION_COSTS_READ },
})

/** applied = Preise stehen in der Liste (ab Stichtag) · pending = wartet auf Freigabe einer zweiten Person */
export const PRICE_CHANGE_OUTCOMES = ['applied', 'pending'] as const

export const priceBulkApply = defineContract({
  name: 'pricing.price.bulkApply',
  title: 'Massenänderung von Preisen anwenden',
  description: 'Wendet eine vorgerechnete Preisänderung an – oder stellt sie zur Freigabe, wenn das Freigaberecht fehlt.',
  llm: {
    description:
      'Wendet eine Massenänderung an (Eingabe wie pricing.price.bulkPreview, dazu ein Name wie „Lieferant +0,60 € ab ' +
      '01.11.“). Hält die Person das Recht „Massenänderungen freigeben“, stehen die neuen Preise sofort ab Stichtag in ' +
      'der Liste (`outcome: applied`); sonst wird die Änderung nur vorbereitet und wartet auf die Freigabe einer ' +
      'zweiten Person (`outcome: pending`) – bis dahin ändert sich kein Preis. Vorher immer bulkPreview zeigen.',
    keywords: ['Massenänderung', 'Preiserhöhung anwenden', 'Preise anheben', 'Preisliste anpassen'],
  },
  input: zBulkRule.extend({ name: zText(120), reason: zOptionalText(500) }),
  output: zBulkSummary.extend({ id: zId, outcome: z.enum(PRICE_CHANGE_OUTCOMES), message: z.string(), labelCount: z.int() }),
  permission: WRITE,
  risk: 'financial',
})

export const PRICE_CHANGE_STATUSES = ['proposed', 'approved', 'rejected', 'withdrawn'] as const

const zPriceChangeRow = z.object({
  id: zId,
  name: z.string(),
  status: z.enum(PRICE_CHANGE_STATUSES),
  priceList: z.object({ id: zId, name: z.string(), priceMode: zPriceMode }),
  validFrom: z.string(),
  reason: z.string().nullable(),
  lineCount: z.int(),
  proposedBy: zPerson,
  proposedAt: z.date(),
  decidedBy: zPerson.nullable(),
  decidedAt: z.date().nullable(),
  decisionNote: z.string().nullable(),
})

export const priceChangeList = defineContract({
  name: 'pricing.priceChange.list',
  title: 'Massenänderungen anzeigen',
  description: 'Vorbereitete, freigegebene und abgelehnte Massenänderungen von Preislisten.',
  llm: {
    description:
      'Listet Massenänderungen von Preislisten mit Zustand (vorbereitet, freigegeben, abgelehnt, zurückgezogen), ' +
      'Stichtag und wer sie vorbereitet bzw. entschieden hat. Filter `status`, `priceListId`. Aktionen und ' +
      'Abschriften stehen unter pricing.promotion.list.',
    keywords: ['Massenänderung', 'Preisänderung offen', 'wartet auf Freigabe', 'Preiserhöhung'],
  },
  input: z.strictObject({ status: z.enum(PRICE_CHANGE_STATUSES).optional(), priceListId: zId.optional(), limit: z.int().min(1).max(200).default(50) }),
  output: z.object({ rows: z.array(zPriceChangeRow) }),
  permission: READ,
  risk: 'read',
})

export const priceChangeGet = defineContract({
  name: 'pricing.priceChange.get',
  title: 'Massenänderung öffnen',
  description: 'Zeigt eine Massenänderung mit allen Zeilen: alter Preis, neuer Preis, Spanne.',
  llm: {
    description:
      'Liefert eine Massenänderung mit ihren Zeilen (alter und neuer Preis je Gebinde und Staffel), der Regel, dem ' +
      'Zustand und ob die Person sie freigeben darf. Spannen nur mit „Einstandspreise sehen“.',
    keywords: ['Massenänderung öffnen', 'Preisänderung prüfen'],
  },
  input: zById,
  output: zPriceChangeRow.extend({
    /** die Regel in einem Satz: „+3,0 %, gerundet auf …,x9“ */
    ruleText: z.string(),
    canApprove: z.boolean(),
    /** warum die Person nicht freigeben darf (eigener Vorschlag) */
    approveHint: z.string().nullable(),
    canWithdraw: z.boolean(),
    rows: z.array(zBulkRow),
  }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { 'rows.*.marginBefore': PERMISSION_COSTS_READ, 'rows.*.marginAfter': PERMISSION_COSTS_READ },
})

const zDecision = z.object({ id: zId, status: z.enum(PRICE_CHANGE_STATUSES), message: z.string(), labelCount: z.int() })

export const priceChangeApprove = defineContract({
  name: 'pricing.priceChange.approve',
  title: 'Massenänderung freigeben',
  description: 'Gibt eine vorbereitete Massenänderung frei: Die neuen Preise stehen ab Stichtag in der Preisliste.',
  llm: {
    description:
      'Gibt eine vorbereitete Massenänderung frei und schreibt ihre Preise ab Stichtag in die Preisliste. Niemand ' +
      'gibt die eigene Änderung frei (Vier-Augen). Liegt der Stichtag inzwischen in der Vergangenheit, gelten die ' +
      'Preise ab heute. Zum Ablehnen pricing.priceChange.reject.',
    keywords: ['Massenänderung freigeben', 'Preiserhöhung freigeben', 'Preisliste freigeben', 'genehmigen'],
  },
  input: z.strictObject({ id: zId, note: zOptionalText(500) }),
  output: zDecision,
  permission: APPROVE,
  risk: 'financial',
})

export const priceChangeReject = defineContract({
  name: 'pricing.priceChange.reject',
  title: 'Massenänderung ablehnen',
  description: 'Lehnt eine vorbereitete Massenänderung mit Grund ab. Kein Preis ändert sich.',
  llm: {
    description: 'Lehnt eine vorbereitete Massenänderung mit Grund ab; kein Preis ändert sich. Nur im Zustand „vorbereitet“.',
    keywords: ['Massenänderung ablehnen', 'Preisänderung ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDecision,
  permission: APPROVE,
  risk: 'write',
})

export const priceChangeWithdraw = defineContract({
  name: 'pricing.priceChange.withdraw',
  title: 'Massenänderung zurückziehen',
  description: 'Zieht eine eigene, noch nicht entschiedene Massenänderung zurück.',
  llm: {
    description: 'Zieht eine vorbereitete Massenänderung zurück, solange niemand entschieden hat. Kein Preis ändert sich.',
    keywords: ['Massenänderung zurückziehen', 'Preisänderung verwerfen'],
  },
  input: zById,
  output: zDecision,
  permission: WRITE,
  risk: 'write',
})

/** Zahl der Preisänderungen und Aktionen, über die die Person entscheiden darf – für Zähler und Startseite. */
export const approvalCount = defineContract({
  name: 'pricing.approval.count',
  title: 'Offene Freigaben zählen',
  description: 'Zählt Aktionen und Massenänderungen, die auf die Freigabe der angemeldeten Person warten.',
  llm: {
    description: 'Zählt die Aktionsvorschläge und Massenänderungen, die die Person freigeben darf (ohne ihre eigenen).',
    keywords: ['offene Freigaben', 'wartet auf mich'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), promotions: z.int(), priceChanges: z.int() }),
  permission: READ,
  risk: 'read',
  assistant: 'never',
})

// ---- Import ----------------------------------------------------------------------------------------------

export const priceUpsert = defineContract({
  name: 'pricing.price.upsert',
  title: 'Preis über Klartext-Schlüssel setzen',
  description: 'Setzt einen Preis über Preisliste (Name), Artikelnummer und Einheit – für den Import einer Preisliste.',
  llm: {
    description:
      'Setzt EINEN Preis über Klartext: Name der Preisliste, Artikelnummer, Einheitencode (z. B. carton, piece; ohne ' +
      'Angabe das Verkaufsgebinde), Betrag in Cent, optional Staffelmenge und Stichtag. Gedacht für den CSV-Import ' +
      '(Einstellungen → Import, „Preisliste“); im Gespräch besser pricing.price.set mit Kennungen.',
    keywords: ['Preis importieren', 'Preisliste importieren'],
  },
  input: z.strictObject({
    priceList: zText(120),
    sku: zText(60),
    unit: zOptionalText(40),
    minQty: zMinQty.default(0),
    amountCents: zPriceCents,
    validFrom: zDay.optional(),
  }),
  output: z.object({ outcome: z.enum(['created', 'updated', 'unchanged']), itemUnitId: zId }),
  permission: WRITE,
  risk: 'financial',
  assistant: 'opt-in',
})

// ---- Ereignis -------------------------------------------------------------------------------------------------

/**
 * Verkaufspreise haben sich geändert (Preiszeile oder Preisliste geschrieben – auch durch Freigaben von
 * Massenänderungen und Aktionen). `itemIds`: betroffene Artikel; [] = viele bzw. alle (ganze Liste). Abnehmer: die
 * Bestell-App (Modul Verkauf → sales.channel.catalogChanged). Je Transaktion höchstens eines je Grund.
 */
export const priceChanged = defineEvent({
  name: 'pricing.price.changed',
  title: 'Preise geändert',
  payload: z.object({ itemIds: z.array(zId).max(500), reason: z.enum(['price', 'promotion', 'list']) }),
})
